import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { login, API_URL } from '../api/api';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { showToast } from '../Layout';
import { Facebook, Globe, Eye, EyeOff, Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import logoWithSlogan from '../imgs/3_trans.png';
import ImageWithFallback from '../components/common/ImageWithFallback';
import SEO from '../components/common/SEO';
import { motion } from 'framer-motion';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();

  const savedEmail = localStorage.getItem('remembered_email');
  
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      email: savedEmail || '',
      rememberMe: !!savedEmail
    }
  });

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const errParam = params.get('error');

    if (token) {
        localStorage.setItem('swapx_user', JSON.stringify({ token }));
        navigate('/profile');
        window.location.reload();
    } else if (errParam) {
        showToast(decodeURIComponent(errParam), 'error');
    }
  }, [location, navigate]);

  const loginMutation = useMutation({
    mutationFn: ({ email, password }) => login(email, password),
    onSuccess: (data, variables) => {
      localStorage.setItem('swapx_user', JSON.stringify(data));
      if (variables.rememberMe) {
        localStorage.setItem('remembered_email', variables.email);
      } else {
        localStorage.removeItem('remembered_email');
      }
      showToast(t('loginSuccess'), 'success');
      navigate('/profile');
      window.location.reload();
    },
    onError: (error) => {
      const errorMsg = error.message === 'Invalid email or password' ? t('invalidEmailPassword') : (t(error.message) || t('authFailed'));
      showToast(errorMsg, 'error');
    },
  });

  const onSubmit = (data) => {
    loginMutation.mutate(data);
  };

  const handleGoogleLogin = () => {
      window.location.href = `${API_URL}/auth/google`;
  };

  const handleFacebookLogin = () => {
      window.location.href = `${API_URL}/auth/facebook`;
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <SEO title={t('login')} description={t('signInToAccount')} />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-card rounded-[3rem] shadow-2xl border border-border overflow-hidden relative"
      >
        {/* Animated Background Glow */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-purple-500 to-secondary opacity-50" />
        
        <div className="p-8 md:p-12">
            <div className="flex flex-col items-center mb-10">
                <Link to="/" className="mb-6 hover:scale-105 transition-transform duration-500">
                    <ImageWithFallback src={logoWithSlogan} alt="Ahlafot" className="h-24 md:h-32 w-auto object-contain" />
                </Link>
                <h2 className="text-3xl font-black uppercase tracking-tighter text-center">
                    {t('signInToAccount')}
                </h2>
                <p className="text-muted-foreground font-bold text-xs uppercase tracking-widest mt-2 opacity-60">
                    {t('welcomeBackBrand', language === 'he' ? 'ברוכים השבים ל-Ahlafot' : 'Welcome back to SwapX')}
                </p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('emailAddress')}</label>
                    <div className="relative group">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors w-5 h-5" />
                        <input
                            type="email"
                            required
                            {...register('email', { required: t('emailRequired') })}
                            className="w-full bg-muted/50 border-none rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 font-bold text-sm transition-all"
                            placeholder={t('emailAddressPlaceholder')}
                        />
                    </div>
                    {errors.email && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 uppercase">{errors.email.message}</p>}
                </div>

                <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('password')}</label>
                    <div className="relative group">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors w-5 h-5" />
                        <input
                            type={showPassword ? "text" : "password"}
                            required
                            {...register('password', { required: t('passwordRequired') })}
                            className="w-full bg-muted/50 border-none rounded-2xl py-4 pl-12 pr-12 focus:ring-2 focus:ring-primary/20 font-bold text-sm transition-all"
                            placeholder="••••••••"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                        >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                    {errors.password && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 uppercase">{errors.password.message}</p>}
                </div>

                <div className="flex items-center justify-between px-1">
                    <label className="flex items-center gap-2 cursor-pointer group">
                        <input type="checkbox" {...register('rememberMe')} className="w-4 h-4 rounded border-border text-primary focus:ring-primary/20 transition-all" />
                        <span className="text-[11px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-foreground transition-colors">{t('rememberMe')}</span>
                    </label>
                </div>

                <button
                    type="submit"
                    disabled={loginMutation.isLoading}
                    className="w-full bg-primary text-primary-foreground py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 mt-4 disabled:opacity-50"
                >
                    {loginMutation.isLoading ? <Loader2 className="animate-spin" /> : (
                        <>
                            {t('signIn')}
                            <ArrowRight size={18} />
                        </>
                    )}
                </button>

                <div className="relative my-8">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border"></div>
                    </div>
                    <div className="relative flex justify-center text-[10px] uppercase font-black tracking-widest">
                        <span className="bg-card px-4 text-muted-foreground opacity-50">{t('orContinueWith')}</span>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <button
                        type="button"
                        onClick={handleGoogleLogin}
                        className="flex items-center justify-center gap-2 py-4 bg-muted/30 rounded-2xl hover:bg-muted transition-all font-black text-[10px] uppercase tracking-widest active:scale-95 border border-transparent hover:border-border"
                    >
                        <Globe size={18} className="text-primary" />
                        {t('google')}
                    </button>
                    <button
                        type="button"
                        onClick={handleFacebookLogin}
                        className="flex items-center justify-center gap-2 py-4 bg-muted/30 rounded-2xl hover:bg-muted transition-all font-black text-[10px] uppercase tracking-widest active:scale-95 border border-transparent hover:border-border"
                    >
                        <Facebook size={18} className="text-[#1877F2]" />
                        {t('facebook')}
                    </button>
                </div>
            </form>

            <div className="mt-10 text-center">
                <p className="text-xs font-bold text-muted-foreground mb-2">{t('noAccount')}</p>
                <Link to="/register" className="text-primary font-black uppercase tracking-[0.15em] text-xs hover:underline hover:scale-105 inline-block transition-transform">
                    {t('register')}
                </Link>
            </div>
        </div>
      </motion.div>
    </div>
  );
}
